import { Fragment, Component, FormEvent } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import update from 'immutability-helper';

import { updateToken } from '../redux/actions';
import {
  PartnerDetailsType,
  UserDataType,
} from '../type-definitions/api-types';

import {
  detailsTabInitElements,
  StateType,
  DetailsTabFormElementsType,
} from '../components/PartnerAdd/helpers';
import axios from 'axios';
import { checkValidation } from '../utils/validation';
import { Col, message, Row, Tabs } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { partnerApi } from '../api-services/api-list';
import { apiCall } from '../api-services/api';
import { handleNotification } from '../utils/notification-handler';
import { partnerRoutes } from '../Routes/routes-list';
import FormWrapper from '../components-shared/FormWrapper';
import DetailsTab from '../components/PartnerAdd/DetailsTab';
import Modal from 'antd/lib/modal/Modal';
import LeafletMap from '../components-shared/LeafletMap';
import PromptPopup from '../components/PromptPopup';

type PropsType = RouteComponentProps &
  PropsFromRedux & {
    userData: Partial<UserDataType>;
    userPermissionList: string[];
  };

class PartnerAdd extends Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);

    const { userData } = props;

    let tempFormElements = update(detailsTabInitElements, {
      latitude: { value: { $set: userData.lat?.toString() || '0' } },
      longitude: { value: { $set: userData.lng?.toString() || '0' } },
    });

    this.state = {
      loadingButton: false,
      currentTabIndex: '1',
      detailsTabFormElements: {
        ...tempFormElements,
      },
      mapZoom: 10,
      showMap: false,
      initLat: userData.lat?.toString() || '0',
      initLng: userData.lng?.toString() || '0',
      initZoom: 10,
      hasInputChanged: false,
    };
  }

  _isMounted = false;
  axiosCancelSource = axios.CancelToken.source();

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.axiosCancelSource.cancel('Component Unmounted');
  }

  handleState = (data: Partial<StateType>, callback?: () => void) => {
    this._isMounted &&
      this.setState(
        (prevState) => {
          return {
            ...prevState,
            ...data,
          };
        },
        () => {
          callback?.();
        }
      );
  };

  inputChangedHandler = (
    name: keyof DetailsTabFormElementsType,
    value: string
  ) => {
    const { detailsTabFormElements } = this.state;

    let tempFormElements = { ...detailsTabFormElements };

    if (
      value &&
      (name === 'latitude' || name === 'longitude') &&
      !checkValidation(value, { isNegativeFloat: true })
    ) {
      message.error('Please enter valid Coordinates');
      return;
    }

    if (name && name !== 'childrenAllowed') {
      tempFormElements = update(tempFormElements, {
        [name]: {
          value: { $set: value },
          touched: { $set: true },
          valid: {
            $set: checkValidation(value, tempFormElements[name].validation),
          },
        },
      });

      this.handleState({
        detailsTabFormElements: { ...tempFormElements },
        hasInputChanged: true,
      });
    }
  };

  handleCheckbox = (event: CheckboxChangeEvent) => {
    const name = event?.target?.name;
    const checked = event?.target?.checked;

    const { detailsTabFormElements } = this.state;

    let tempFormElements = { ...detailsTabFormElements };

    if (name === 'childrenAllowed') {
      tempFormElements = update(tempFormElements, {
        [name]: {
          $set: checked,
        },
      });

      this.handleState({
        detailsTabFormElements: { ...tempFormElements },
        hasInputChanged: true,
      });
    }
  };

  onFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    this.handleSubmit();
  };

  handleSubmit = async (onConfirm?: () => void) => {
    const { detailsTabFormElements } = this.state;
    const { userData } = this.props;

    let tempFormElements = { ...detailsTabFormElements };

    let key: keyof typeof tempFormElements;

    for (key in tempFormElements) {
      if (key !== 'childrenAllowed') {
        tempFormElements = update(tempFormElements, {
          [key]: {
            touched: { $set: true },
            valid: {
              $set: checkValidation(
                tempFormElements[key].value,
                tempFormElements[key].validation
              ),
            },
          },
        });
      }
    }

    this.handleState({ detailsTabFormElements: tempFormElements });

    for (key in tempFormElements) {
      if (key !== 'childrenAllowed' && !tempFormElements[key].valid) {
        message.error('Please fill all the fields');
        return;
      }
    }

    this.handleState({ loadingButton: true });

    try {
      const { url, method, contentType } = partnerApi.postPartnerDetails();

      const data: Partial<PartnerDetailsType> = {
        partnerid: tempFormElements.partnerID.value,
        partnername: tempFormElements.partnerName.value,
        lat: Number(tempFormElements.latitude.value),
        lng: Number(tempFormElements.longitude.value),
        prefix: tempFormElements.prefix.value,
        childrenAllowed: tempFormElements.childrenAllowed,
      };

      const response = await apiCall({
        storeToken: userData?.token,
        url,
        method,
        contentType,
        data,
        cancelToken: this.axiosCancelSource.token,
      });

      const result = response?.data;

      if (result?.status === 'ok') {
        this._isMounted && handleNotification('success', result);
        this.handleState(
          { loadingButton: false, hasInputChanged: false },
          () => {
            const { history } = this.props;
            setTimeout(() => {
              history.push({
                pathname: partnerRoutes.details(),
                state: {
                  partnerID: tempFormElements.partnerID.value,
                  allowEdit: true,
                },
              });
            }, 1000);
          }
        );
      } else {
        this._isMounted && handleNotification('error', result);
        this.handleState({ loadingButton: false });
      }
    } catch (error) {
      this._isMounted && handleNotification('error', error?.data);
      this.handleState({ loadingButton: false });
    }
  };

  handlePopupConfirm = (onConfirm: () => void) => {
    this.handleSubmit(onConfirm);
  };

  setCurrentTabIndex = (index: string) => {
    this.handleState({ currentTabIndex: index });
  };

  onOpenMapModal = () => {
    this.handleState({ showMap: true });
  };

  onCancelMapModal = () => {
    const { detailsTabFormElements, initLat, initLng, initZoom } = this.state;

    const tempFormElements = update(detailsTabFormElements, {
      latitude: { value: { $set: initLat } },
      longitude: { value: { $set: initLng } },
    });

    this.handleState({
      showMap: false,
      detailsTabFormElements: { ...tempFormElements },
      mapZoom: initZoom,
    });
  };

  onOkayMapModal = () => {
    const { detailsTabFormElements, mapZoom } = this.state;

    this.handleState({
      showMap: false,
      initLat: detailsTabFormElements.latitude.value,
      initLng: detailsTabFormElements.longitude.value,
      initZoom: mapZoom,
    });
  };

  onMapZoom = (zoom: number) => {
    this.handleState({ mapZoom: zoom });
  };

  onMapClickMarkerMove = (lat: number, lng: number) => {
    const { detailsTabFormElements } = this.state;

    const tempFormElements = update(detailsTabFormElements, {
      latitude: { value: { $set: lat.toString() } },
      longitude: { value: { $set: lng.toString() } },
    });

    this.handleState({
      detailsTabFormElements: tempFormElements,
      hasInputChanged: true,
    });
  };

  handleCancelRedirect = () => {
    const { history } = this.props;
    history.push({
      pathname: partnerRoutes.list,
    });
  };

  render() {
    const {
      currentTabIndex,
      detailsTabFormElements,
      showMap,
      mapZoom,
      loadingButton,
      hasInputChanged,
    } = this.state;
    const { userPermissionList } = this.props;

    if (userPermissionList.length === 0) {
      return <Redirect to="/" />;
    }

    return (
      <Fragment>
        {hasInputChanged && (
          <PromptPopup
            when={hasInputChanged}
            handleConfirm={this.handlePopupConfirm}
          />
        )}
        <FormWrapper title={`Partner Add`}>
          <form noValidate onSubmit={this.onFormSubmit}>
            <Tabs
              activeKey={currentTabIndex}
              onTabClick={this.setCurrentTabIndex}>
              <Tabs.TabPane key="1" tab="Details">
                <DetailsTab
                  formElements={detailsTabFormElements}
                  inputChangedHandler={this.inputChangedHandler}
                  handleCheckbox={this.handleCheckbox}
                  onOpenMapModal={this.onOpenMapModal}
                  handleCancelRedirect={this.handleCancelRedirect}
                  loadingButton={loadingButton}
                />
              </Tabs.TabPane>
            </Tabs>
          </form>
        </FormWrapper>

        {showMap && (
          <Modal
            width={'60%'}
            visible={showMap}
            onCancel={this.onCancelMapModal}
            closable={false}
            onOk={this.onOkayMapModal}>
            <Row>
              <Col xs={24}>
                <LeafletMap
                  onMapClickMarkerMove={this.onMapClickMarkerMove}
                  onMapZoom={this.onMapZoom}
                  lat={parseFloat(detailsTabFormElements.latitude.value)}
                  lng={parseFloat(detailsTabFormElements.longitude.value)}
                  zoom={mapZoom}
                  height={'50vh'}
                />
              </Col>
            </Row>
          </Modal>
        )}
      </Fragment>
    );
  }
}

const mapDispatch = {
  updateToken: updateToken,
};

const connector = connect(null, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PartnerAdd);
